<template>
	<div class="l-matrix" :style="matrixStyle" data-testid="status-matrix">
		<div class="l-matrix-scroll" tabindex="0" :aria-label="ariaLabel || undefined">
			<table class="l-matrix-table">
				<thead>
					<tr>
						<th scope="col" class="l-matrix-corner">
							<slot name="header-row-header">{{ rowHeaderLabel }}</slot>
						</th>

						<th v-for="column in columns" :key="column.key" scope="col" class="l-matrix-colhead">
							<slot name="column-header" :column="column">
								<span class="l-matrix-collabel">{{ column.label }}</span>
								<span v-if="column.sub" class="l-matrix-colsub">{{ column.sub }}</span>
							</slot>
						</th>

						<th v-if="hasTotal" scope="col" class="l-matrix-totalhead">
							<slot name="header-total">{{ totalLabel }}</slot>
						</th>
					</tr>
				</thead>

				<tbody>
					<tr v-for="row in rows" :key="row.key">
						<th scope="row" class="l-matrix-rowhead">
							<slot name="row-header" :row="row" />
						</th>

						<td v-for="column in columns" :key="column.key" class="l-matrix-cell">
							<button
								v-if="isCellClickable(row, column)"
								type="button"
								class="l-matrix-cellbtn"
								@click="onCellClick(row, column)"
							>
								<slot name="cell" :row="row" :column="column" />
							</button>
							<div v-else class="l-matrix-cellbody">
								<slot name="cell" :row="row" :column="column" />
							</div>
						</td>

						<td v-if="hasTotal" class="l-matrix-total">
							<slot name="row-total" :row="row" />
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>










<script setup>
/**
 * §8.6 StatusMatrix: a grid of people (rows) against dates or periods (columns), where each cell
 * is a status.
 *
 * The first column (the row header) and the last column (the total) stay fixed; the columns in
 * between scroll horizontally when there are more than fit, so a 20-column matrix is as readable
 * as a 5-column one. It is a real table: row and column headers are `th`s with a scope.
 *
 * The component draws the structure and nothing else. What a cell, a row header and a total look
 * like is the page's, through slots. A cell is a button (focusable, keyboard-operable) only when
 * `cellClickable(row, column)` says so; otherwise it is plain content.
 *
 * `columns` is `[{ key, label, sub }]` (`sub` is an optional second line, such as a date under a
 * weekday); `rows` is `[{ key, ... }]`, with whatever else the slots need.
 */

import { computed, useSlots } from 'vue';

//-- Props
const props = defineProps({
	columns: {
		type: Array,
		required: true,
	},

	rows: {
		type: Array,
		required: true,
	},

	/** What the table is, for a screen reader. */
	ariaLabel: {
		type: String,
		default: '',
	},

	/** The heading of the fixed first column, unless the `#header-row-header` slot replaces it. */
	rowHeaderLabel: {
		type: String,
		default: '',
	},

	/** The heading of the fixed last column, unless the `#header-total` slot replaces it. */
	totalLabel: {
		type: String,
		default: 'Total',
	},

	/** Whether a cell is a button: `(row, column) => boolean`. Default: none are. */
	cellClickable: {
		type: Function,
		default: null,
	},

	/** The narrowest a scrolling column may be, in px. */
	columnMinWidth: {
		type: Number,
		default: 84,
	},

	/** The width of the fixed first column, in px. */
	rowHeaderWidth: {
		type: Number,
		default: 220,
	},

	/** The width of the fixed last column, in px. */
	totalWidth: {
		type: Number,
		default: 96,
	},
});





//-- Emits
const emit = defineEmits(['cell-click']);





//-- Data/Reactive State
const slots = useSlots();





//-- Computed Properties
const hasTotal = computed(() => !!slots['row-total']);

const matrixStyle = computed(() => ({
	'--matrix-col-min': props.columnMinWidth + 'px',
	'--matrix-rowhead-w': props.rowHeaderWidth + 'px',
	'--matrix-total-w': props.totalWidth + 'px',
}));





//-- Methods
const isCellClickable = (row, column) =>
{
	return props.cellClickable ? props.cellClickable(row, column) === true : false;
};


const onCellClick = (row, column) =>
{
	emit('cell-click', { row, column });
};
</script>










<style scoped>
.l-matrix
{
	border: 1px solid var(--line);
	border-radius: var(--r-lg);
	background: var(--surface);
	overflow: hidden;
}

.l-matrix-scroll
{
	overflow-x: auto;
}

.l-matrix-table
{
	border-collapse: separate;
	border-spacing: 0;
	width: 100%;
	min-width: max-content;
}

/* Headers */
.l-matrix-colhead,
.l-matrix-totalhead,
.l-matrix-corner
{
	padding: 10px 8px;
	text-align: center;
	vertical-align: bottom;
	font-size: var(--text-eyebrow);
	font-weight: 600;
	letter-spacing: 0.6px;
	text-transform: uppercase;
	color: var(--text-3);
	background: var(--surface-sunken);
	border-bottom: 1px solid var(--line-strong);
}

.l-matrix-colhead
{
	min-width: var(--matrix-col-min);
}

.l-matrix-collabel
{
	display: block;
}

.l-matrix-colsub
{
	display: block;
	margin-top: 2px;
	font-weight: 500;
	letter-spacing: 0;
	text-transform: none;
	font-variant-numeric: tabular-nums;
}

/* The two fixed columns. Opaque backgrounds, so scrolled columns slide beneath them. */
.l-matrix-corner,
.l-matrix-rowhead
{
	position: sticky;
	left: 0;
	z-index: 2;
	width: var(--matrix-rowhead-w);
	min-width: var(--matrix-rowhead-w);
	max-width: var(--matrix-rowhead-w);
	text-align: left;
	border-right: 1px solid var(--line);
}

/* The fixed columns never take more than a share of a narrow screen, so there is always room to scroll the middle. */
.l-matrix-corner,
.l-matrix-rowhead
{
	width: min(var(--matrix-rowhead-w), 34vw);
	min-width: min(var(--matrix-rowhead-w), 34vw);
	max-width: min(var(--matrix-rowhead-w), 34vw);
}

.l-matrix-totalhead,
.l-matrix-total
{
	position: sticky;
	right: 0;
	z-index: 2;
	width: min(var(--matrix-total-w), 22vw);
	min-width: min(var(--matrix-total-w), 22vw);
	border-left: 1px solid var(--line);
}

.l-matrix-corner,
.l-matrix-totalhead
{
	z-index: 3;
}

.l-matrix-rowhead
{
	padding: 8px 12px;
	font-weight: 400;
	background: var(--surface);
	border-bottom: 1px solid var(--line-soft);
}

.l-matrix-total
{
	padding: 8px 12px;
	text-align: right;
	font-size: var(--text-sm);
	font-variant-numeric: tabular-nums;
	font-weight: 600;
	color: var(--text);
	background: var(--surface);
	border-bottom: 1px solid var(--line-soft);
}

/* Cells */
.l-matrix-cell
{
	padding: 3px;
	min-width: var(--matrix-col-min);
	border-bottom: 1px solid var(--line-soft);
	vertical-align: middle;
}

.l-matrix-cellbody,
.l-matrix-cellbtn
{
	display: block;
	width: 100%;
	height: 100%;
	min-height: 44px;
}

.l-matrix-cellbtn
{
	padding: 0;
	border: 0;
	border-radius: var(--r-sm);
	background: none;
	font: inherit;
	color: inherit;
	text-align: inherit;
	cursor: pointer;
}

.l-matrix-cellbtn:hover
{
	box-shadow: inset 0 0 0 2px var(--interactive);
}

.l-matrix-cellbtn:focus-visible,
.l-matrix-scroll:focus-visible
{
	outline: 2px solid var(--interactive);
	outline-offset: -2px;
}

tbody tr:last-child .l-matrix-cell,
tbody tr:last-child .l-matrix-rowhead,
tbody tr:last-child .l-matrix-total
{
	border-bottom: 0;
}
</style>
